import type { ComponentPropsWithoutRef } from "react";
import type { Control } from "react-hook-form";
import { t } from "ttag";

import type { Category } from "@/shared/domains";
import { Form, HomeIcon, Select } from "@/shared/ui";
import { useStateOptions } from "../hooks";

interface FieldValue {
  state: string;
}

type StateControl<T> = T extends FieldValue ? Control<T> : never;

type StateFieldProps<T extends FieldValue> = ComponentPropsWithoutRef<
  typeof Form.Item
> & {
  control: StateControl<T>;
  categorySlug?: Category["slug"];
  onValueChange?: VoidFunction;
  contentClassName?: string;
};

export const StateField = <T extends FieldValue>({
  control,
  categorySlug,
  onValueChange,
  contentClassName,
  ...props
}: StateFieldProps<T>) => {
  const { data: stateOptions, isLoading } = useStateOptions(categorySlug);

  return (
    <Form.Field
      control={control}
      name="state"
      render={({ field: { value, onChange, ...field } }) => (
        <Form.Item id="state" {...props}>
          <Form.Label aria-required>{t`State`}</Form.Label>
          <Form.Control>
            <Select
              value={value ?? ""}
              options={stateOptions}
              isLoading={isLoading}
              placeholder={t`Select a state`}
              leftIcon={<HomeIcon />}
              contentClassName={contentClassName}
              onChange={(val) => {
                onChange(val);
                onValueChange?.();
              }}
              {...field}
            />
          </Form.Control>
          <Form.Message compact={false} />
        </Form.Item>
      )}
    />
  );
};
